// Push events → what the export screen draws.
//
// The push core reports primitives (a stage moved; a sample changed state).
// The screen wants a bar, a sub-line, and one row per sample. That translation
// is here, as a pure reducer, so it can be tested without a browser.
//
// The one subtlety is row identity. A sample is known by the seed's audioKey
// while it is being fetched and by its content tag once it is being uploaded,
// so a `downloaded` event carries both and the reducer keeps the alias. Without
// that, every sample would appear twice — once stuck at "downloading".

import type { PushEvent, PushStage, SampleState } from "@audiotool-core/progress.js";

export type { PushEvent, PushStage, SampleState };

export interface SampleRow {
  id: string;
  displayName: string;
  bytes: number;
  state: SampleState;
  /** Clips sharing this one sample. The audio is uploaded once. */
  uses: number;
}

export interface ExportProgress {
  stage: PushStage | "idle";
  done: number;
  total: number;
  /** Floor for the bar. The screen may creep above it while a stage is silent. */
  percent: number;
  samples: SampleRow[];
  /** audioKey → row id, so upload events find the row the download made. */
  aliases: Record<string, string>;
}

export const NO_PROGRESS: ExportProgress = { stage: "idle", done: 0, total: 0, percent: 0, samples: [], aliases: {} };

/**
 * Where each stage sits on the bar. Fetching and uploading own the bulk because
 * they are the slow parts and the only ones that can count themselves; writing
 * holds the document lock in batches it can't usefully count, so it gets a band
 * the screen creeps through rather than a counter.
 */
const BANDS: Record<PushStage, [number, number]> = {
  // Seed → Audiotool.
  project: [0, 5],
  "fetching-audio": [5, 35],
  "fetching-stems": [5, 35],
  uploading: [35, 75],
  writing: [75, 97],
  // Audiotool → seed. Encoding earns its share: LAME over every clip is the
  // slowest thing this app asks a tab to do.
  reading: [0, 5],
  rendering: [5, 30],
  downloading: [30, 40],
  cutting: [40, 50],
  mixing: [50, 60],
  encoding: [60, 75],
  publishing: [75, 97],
  done: [100, 100],
};

export function stageFloor(stage: PushStage, done: number, total: number): number {
  const [from, to] = BANDS[stage];
  if (total <= 0) return from;
  return Math.round(from + ((to - from) * Math.min(done, total)) / total);
}

export function applyPushEvent(state: ExportProgress, event: PushEvent): ExportProgress {
  if (event.kind === "stage") {
    const done = event.done ?? 0;
    const total = event.total ?? 0;
    return {
      ...state,
      stage: event.stage,
      done,
      total,
      // A sample that was fetched but never uploaded is one the account already
      // held — there was nothing to create for it. Settle those at the end, or
      // they spin forever on a finished screen.
      samples: event.stage === "done"
        ? state.samples.map((s) => (s.state === "downloaded" || s.state === "downloading" ? { ...s, state: "reused" as const } : s))
        : state.samples,
      // Never go backwards: stages overlap in time (a sample uploads while the
      // next is still being fetched) and a bar that retreats reads as a fault.
      percent: Math.max(state.percent, stageFloor(event.stage, done, total)),
    };
  }

  const id = state.aliases[event.id] ?? event.id;
  const aliases = event.tag && event.tag !== id ? { ...state.aliases, [event.tag]: id } : state.aliases;
  const at = state.samples.findIndex((s) => s.id === id);
  const prev = at >= 0 ? state.samples[at]! : null;

  const row: SampleRow = {
    id,
    // A later event may carry a better name (the clip's, not the key's) — take
    // it, but never fall back to a worse one.
    displayName: named(event.displayName) ? event.displayName : (prev?.displayName ?? event.displayName),
    bytes: event.bytes ?? prev?.bytes ?? 0,
    state: event.state,
    uses: prev && named(event.displayName) && named(prev.displayName) && prev.displayName !== event.displayName
      ? prev.uses + 1
      : (prev?.uses ?? 1),
  };
  const samples = at >= 0 ? state.samples.map((s, i) => (i === at ? row : s)) : [...state.samples, row];
  return { ...state, samples, aliases };
}

/** A name a person would recognise, rather than an audioKey or a content tag. */
function named(displayName: string): boolean {
  return displayName.includes(" ");
}

/** The sub-line under the bar. */
export function stageText(p: ExportProgress): string {
  switch (p.stage) {
    case "idle":
      return "Preparing…";
    case "project":
      return "Creating the project…";
    case "fetching-audio":
      return p.total > 0 ? `Downloading audio (${p.done}/${p.total})` : "Downloading audio…";
    case "fetching-stems":
      return p.total > 0 ? `Downloading stems (${p.done}/${p.total})` : "Downloading stems…";
    case "uploading":
      if (p.total === 0) return "Everything is already in your Audiotool library";
      return p.done > 0 ? `Uploading to Audiotool (${p.done}/${p.total})` : `Uploading to Audiotool (0/${p.total})`;
    case "writing":
      return "Building the project…";
    case "reading":
      return "Reading the Audiotool project…";
    case "rendering":
      return p.total > 0 ? `Audiotool is rendering (${p.done}/${p.total})` : "Asking Audiotool to render…";
    case "downloading":
      return p.total > 0 ? `Downloading samples (${p.done}/${p.total})` : "Downloading samples…";
    case "cutting":
      return "Cutting the clips…";
    case "mixing":
      return "Rendering the master mix…";
    case "encoding":
      return "Encoding previews…";
    case "publishing":
      return p.total > 0 ? `Uploading to Seedbed (${p.done}/${p.total})` : "Uploading to Seedbed…";
    case "done":
      return "Done.";
  }
}

/** Is this sample finished with, one way or another? */
export function settled(state: SampleState): boolean {
  return state === "uploaded" || state === "reused" || state === "failed" || state === "missing";
}
